import { useEffect } from 'react';
import { supabase } from '../lib/supabase';

// Tawk.to live chat widget.
// Property/widget IDs from the embed snippet provided by Tawk.to.
const TAWK_SRC = 'https://embed.tawk.to/6a45ed98113c4b1d489fdb49/1jsgig7hb';

export interface SupportChatContext {
  /** Visitor name shown at the top of the operator panel. */
  name?: string;
  /** Visitor email shown to the operator (client-side, not verified). */
  email?: string;
  /** Custom key/value pairs shown in the visitor info panel. Values are stringified. */
  attributes?: Record<string, string | number | null | undefined>;
  /** Tags applied to the conversation (e.g. for filtering). */
  tags?: string[];
  /** A timeline event logged in the chat. */
  event?: { name: string; metadata?: Record<string, unknown> };
}

interface TawkApi {
  showWidget?: () => void;
  hideWidget?: () => void;
  maximize?: () => void;
  setAttributes?: (attrs: Record<string, string>, cb?: (err?: unknown) => void) => void;
  addTags?: (tags: string[], cb?: (err?: unknown) => void) => void;
  addEvent?: (event: string, metadata?: Record<string, unknown>, cb?: (err?: unknown) => void) => void;
  onLoad?: () => void;
  onChatMinimized?: () => void;
  onChatEnded?: () => void;
  [key: string]: unknown;
}

declare global {
  interface Window {
    Tawk_API?: TawkApi;
    Tawk_LoadStart?: Date;
  }
}

// Only inject the third-party script once per page load.
let scriptInjected = false;
// Whether Tawk.to has finished loading and its API methods are callable.
let widgetLoaded = false;
// A support-chat open request made before the widget finished loading. `undefined`
// means "no pending request"; `null` means "open with no context".
let pendingOpen: SupportChatContext | null | undefined = undefined;

function applyContext(api: TawkApi, context?: SupportChatContext | null) {
  if (!context) return;

  // Order/session attributes. Email is handled by secureIdentify() so that,
  // when Secure Mode is enabled, we only ever send email together with its hash.
  const attrs: Record<string, string> = {};
  if (context.name) attrs.name = context.name;
  for (const [key, value] of Object.entries(context.attributes ?? {})) {
    if (value !== null && value !== undefined && value !== '') attrs[key] = String(value);
  }
  if (Object.keys(attrs).length > 0) api.setAttributes?.(attrs, () => {});

  if (context.tags?.length) api.addTags?.(context.tags, () => {});
  if (context.event) api.addEvent?.(context.event.name, context.event.metadata, () => {});
}

/**
 * Verify the visitor's identity for Tawk Secure Mode. Fetches an HMAC of the
 * logged-in user's email from the tawk-visitor-hash edge function and sets
 * { email, hash } so Tawk marks the email as verified. Best-effort: if the
 * function isn't reachable (or the visitor is anonymous), falls back to the
 * client-supplied email as unverified so the operator still sees something.
 */
async function secureIdentify(api: TawkApi, fallbackEmail?: string) {
  try {
    const { data, error } = await supabase.functions.invoke('tawk-visitor-hash');
    if (!error && data?.email && data?.hash) {
      const attrs: Record<string, string> = { email: data.email, hash: data.hash };
      if (data.name) attrs.name = data.name;
      api.setAttributes?.(attrs, () => {});
      return;
    }
  } catch {
    // ignore — fall through to unverified fallback
  }
  if (fallbackEmail) api.setAttributes?.({ email: fallbackEmail }, () => {});
}

/**
 * Open the live chat, optionally passing context (visitor identity, order
 * details, tags) so the operator has it before the customer types. If the
 * widget hasn't finished loading yet, the request is queued and fired on load.
 */
export function openSupportChat(context?: SupportChatContext) {
  const api = window.Tawk_API;
  if (widgetLoaded && api && typeof api.maximize === 'function') {
    api.showWidget?.();
    applyContext(api, context);
    api.maximize();
    void secureIdentify(api, context?.email);
    return;
  }
  // Widget not ready yet — remember the request (and context) and open on load.
  pendingOpen = context ?? null;
}

/**
 * Loads the Tawk.to widget on customer-facing pages, but keeps the floating
 * launcher HIDDEN. The chat is only opened via openSupportChat() (e.g. the
 * "Contact Support" link), and re-hidden when the customer closes it.
 * Pass `enabled={false}` on business/staff routes so it never loads there.
 */
export default function TawkChat({ enabled }: { enabled: boolean }) {
  // Inject the script the first time we're on a page where chat is allowed.
  useEffect(() => {
    if (!enabled || scriptInjected) return;
    scriptInjected = true;

    const api = (window.Tawk_API = window.Tawk_API || {});
    window.Tawk_LoadStart = new Date();

    const prevOnLoad = api.onLoad;
    api.onLoad = () => {
      if (typeof prevOnLoad === 'function') prevOnLoad();
      widgetLoaded = true;
      // No persistent bubble — hidden until the user clicks Contact Support.
      api.hideWidget?.();

      if (pendingOpen !== undefined) {
        const ctx = pendingOpen;
        pendingOpen = undefined;
        api.showWidget?.();
        applyContext(api, ctx);
        api.maximize?.();
        void secureIdentify(api, ctx?.email);
      }
    };

    // Keep the launcher hidden again after the customer closes the chat.
    const rehide = () => api.hideWidget?.();
    api.onChatMinimized = rehide;
    api.onChatEnded = rehide;

    const s1 = document.createElement('script');
    s1.async = true;
    s1.src = TAWK_SRC;
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');

    const s0 = document.getElementsByTagName('script')[0];
    s0?.parentNode?.insertBefore(s1, s0);
  }, [enabled]);

  return null;
}
